import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Document } from '../../../models/document.model';
import { onError } from '../../../utils/error.util';
import { createDocument, getDocument, updateDocument } from '../../../services/documents.service';

import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';

import { CircularProgressComponent } from '../../../components/admin/circular-progress.component';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

const DocumentSchema = Yup.object().shape({
  title: Yup.string().required('Title is required'),
  htmlContent: Yup.string().required('HTML content is required'),
});

const documentInitialValues = {
  id: '',
  title: '',
  htmlContent: '',
  createdAt: 0,
  updatedAt: 0,
};

export default function DocumentFormContainer() {
  const navigate = useNavigate();
  const { documentId } = useParams();
  const [document, setDocument] = useState<Document>(documentInitialValues);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    async function onLoad() {
      if (!documentId) {
        setIsLoading(false);
        return;
      }

      if (documentId === 'new') {
        setIsLoading(false);
        return;
      }

      try {
        const response = await getDocument(documentId);
        setDocument(response);
      } catch (e) {
        onError(e);
      }

      setIsLoading(false);
    }

    onLoad();
  }, [documentId]);

  const onSubmit = async (values: any) => {
    setIsSubmitting(true);
    try {
      if (documentId === 'new') {
        await createDocument({
          title: values.title,
          htmlContent: values.htmlContent,
        });
      } else {
        await updateDocument({
          ...document,
          title: values.title,
          htmlContent: values.htmlContent,
        });
      }
      navigate('/documents');
    } catch (e: any) {
      onError(e);
      setIsSubmitting(false);
    }
  };

  function renderForm() {
    return (
      <Container>
        <Formik
          initialValues={document}
          validationSchema={DocumentSchema}
          onSubmit={onSubmit}
          enableReinitialize={true}
        >
          {({ values, errors, touched, handleChange, handleBlur }) => (
            <Form>
              <Box sx={{ mb: 3 }}>
                <h1>{documentId === 'new' ? 'Create Document' : 'Edit Document'}</h1>
              </Box>

              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    id="title"
                    name="title"
                    label="Title"
                    value={values.title}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.title && Boolean(errors.title)}
                    helperText={touched.title && errors.title}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={15}
                    id="htmlContent"
                    name="htmlContent"
                    label="HTML Content"
                    value={values.htmlContent}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.htmlContent && Boolean(errors.htmlContent)}
                    helperText={touched.htmlContent && errors.htmlContent}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
                      {isSubmitting ? 'Saving...' : 'Save'}
                    </Button>
                    <Button variant="outlined" onClick={() => navigate('/documents')} disabled={isSubmitting}>
                      Cancel
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Container>
    );
  }

  return <main id="main">{isLoading ? <CircularProgressComponent /> : renderForm()}</main>;
}

